import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { seriesColor, useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { buildFrame, foldToOther, frameNote, isEmpty } from '../frame';
import {
  animation,
  BAR_MAX_WIDTH,
  barRadius,
  baseGrid,
  categoryAxisX,
  categoryAxisY,
  emphasisFocus,
  itemTooltip,
  axisTooltip,
  legendFor,
  stackGap,
  valueAxisX,
  valueAxisY,
  valueLabel,
} from '../options';

/**
 * Bars and columns — the form for comparing a measure across categories.
 *
 * Horizontal bars when the categories are names (countries, apps, publishers):
 * long labels get a whole left gutter instead of being rotated 45° and
 * truncated. Vertical columns when the categories are ordered buckets — time
 * periods, ranks, bands — where left-to-right already means something.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

/** Bars carry one measure each, so a long tail is noise. */
const BAR_LIMIT = 20;
const COLUMN_LIMIT = 40;
/** The palette has eight validated slots and no ninth. */
const MAX_SERIES = 8;
/** Above this, per-bar value labels collide; the axis and tooltip take over. */
const LABEL_LIMIT = 14;

export const BarChart = ({ data, fields }: Props) => {
  const theme = useTheme();

  const view = useMemo(() => {
    const frame = foldToOther(buildFrame(data, { limit: BAR_LIMIT, ...fields }), MAX_SERIES);
    if (isEmpty(frame)) return null;

    const single = frame.series.length === 1;
    // Every value labelled means the axis is repeating itself — drop it and let
    // the labels carry the numbers.
    const labelled = single && frame.categories.length <= LABEL_LIMIT;

    const option: EChartsOption = {
      ...animation,
      // Room on the right for the value labels, or for the last axis tick,
      // which is centred on the plot edge and would otherwise be clipped.
      grid: { ...baseGrid({ legend: !single }), left: 4, right: labelled ? 52 : 28 },
      xAxis: labelled
        ? { type: 'value' as const, show: false }
        : valueAxisX(theme, { name: frame.measureName }),
      yAxis: categoryAxisY(theme, frame),
      legend: legendFor(theme, frame.series.length),
      tooltip: single ? itemTooltip(theme) : axisTooltip(theme, frame),
      series: frame.series.map((s, i) => ({
        type: 'bar' as const,
        name: s.name,
        data: s.values,
        barMaxWidth: BAR_MAX_WIDTH,
        barCategoryGap: '38%',
        barGap: '12%',
        itemStyle: {
          color: seriesColor(theme, i),
          borderRadius: barRadius('horizontal', false),
        },
        label: labelled ? valueLabel(theme, 'right') : { show: false },
        emphasis: emphasisFocus,
      })),
    };

    return { option, note: frameNote(frame) };
  }, [data, theme]);

  if (!view) return <Empty />;

  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Bar chart" />
    </Plot>
  );
};

const columnView = (
  data: ChartData,
  fields: ChartFields | undefined,
  theme: ReturnType<typeof useTheme>,
  stacked: boolean,
): { option: EChartsOption; note?: string } | null => {
  const frame = foldToOther(buildFrame(data, { limit: COLUMN_LIMIT, ...fields }), MAX_SERIES);
  if (isEmpty(frame)) return null;

  const single = frame.series.length === 1;
  const crowded = frame.categories.length > 12;

  const option: EChartsOption = {
    ...animation,
    grid: baseGrid({ legend: !single, rotatedLabels: crowded && !frame.temporal }),
    xAxis: categoryAxisX(theme, frame, { rotate: crowded && !frame.temporal }),
    yAxis: valueAxisY(theme, { name: frame.measureName }),
    legend: legendFor(theme, frame.series.length),
    // Stacked columns read as one total per category, so the crosshair readout
    // covering every series is the right hover; grouped bars are hit directly.
    tooltip: stacked || !single ? axisTooltip(theme, frame) : itemTooltip(theme),
    series: frame.series.map((s, i) => ({
      type: 'bar' as const,
      name: s.name,
      data: s.values,
      stack: stacked ? 'total' : undefined,
      barMaxWidth: BAR_MAX_WIDTH,
      barCategoryGap: '38%',
      barGap: '12%',
      itemStyle: {
        color: seriesColor(theme, i),
        borderRadius: barRadius('vertical', stacked),
        // Stacked segments touch; the 2px surface gap separates them.
        ...(stacked ? stackGap(theme) : {}),
      },
      emphasis: emphasisFocus,
    })),
  };

  return { option, note: frameNote(frame) };
};

export const ColumnChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => columnView(data, fields, theme, false), [data, theme, fields]);

  if (!view) return <Empty />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Column chart" />
    </Plot>
  );
};

export const StackedColumnChart = ({ data, fields }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => columnView(data, fields, theme, true), [data, theme, fields]);

  if (!view) return <Empty />;
  return (
    <Plot note={view.note}>
      <EChart option={view.option} ariaLabel="Stacked column chart" />
    </Plot>
  );
};
